import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CreditCard,
  Landmark,
  ShieldCheck,
  Settings2,
  ArrowRight,
  Sparkles,
  ChevronDown,
} from "lucide-react";


// Same theme tokens as DevelopersHubMenu — light panel, no dark-gradient
// background, so all nav dropdowns read as one consistent system.
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  border: "#E6DFDC",
  text: "#FFF8F6",
  textMuted: "#D8B4B8",
  panelBg: "#FFFFFF",
  cardBg: "#F6EEEC",
};

const menuItems = [
  {
    to: "/products/payment-gateway",
    icon: CreditCard,
    title: "Payment Gateway",
    desc: "Accept online payments securely and instantly.",
  },
  {
    to: "/products/banking-apis",
    icon: Landmark,
    title: "Banking APIs",
    desc: "Connect seamlessly with banking infrastructure.",
  },
  {
    to: "/products/verification-suite",
    icon: ShieldCheck,
    title: "Verification Suite",
    desc: "KYC, PAN, Aadhaar & business verification.",
  },
  {
    to: "/products/automation-tools",
    icon: Settings2,
    title: "Automation Tools",
    desc: "Automate workflows and reduce manual effort.",
  },
];

export default function ProductsMenu() {
  const [productsOpen, setProductsOpen] = useState(false);

  return (
    <li
      className="relative"
      onMouseEnter={() => setProductsOpen(true)}
      onMouseLeave={() => setProductsOpen(false)}
    >
      <span
        className="flex items-center gap-1.5 py-1 transition-colors"
        style={{ color: productsOpen ? theme.text : theme.textMuted }}
      >
        <Link to="/products" data-cursor="hover" className="hover:text-[#FFF8F6]">
          Products
        </Link>
        <ChevronDown size={14} className={`transition-transform duration-300 ${productsOpen ? "rotate-180" : ""}`} />
      </span>

      <AnimatePresence>
        {productsOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-1/2 -translate-x-1/2 mt-6 w-[800px] max-w-[calc(100vw-2rem)] rounded-3xl shadow-2xl z-50 overflow-hidden"
            style={{ backgroundColor: theme.panelBg, border: `1px solid ${theme.border}` }}
          >
            <div className="relative grid grid-cols-3 gap-8 p-10">
              <div className="col-span-2 grid grid-cols-2 gap-6">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      data-cursor="hover"
                      className="group p-5 rounded-2xl border transition-colors duration-300 hover:bg-[#F6EEEC]"
                      style={{ borderColor: theme.border }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = `${theme.accent}80`)}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = theme.border)}
                    >
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-colors"
                        style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                      >
                        <Icon size={20} />
                      </div>

                      <h4 className="font-semibold transition-colors" style={{ color: theme.ink }}>
                        <span className="group-hover:text-[color:var(--accent-hover)]" style={{ "--accent-hover": theme.accent }}>
                          {item.title}
                        </span>
                      </h4>

                      <p className="text-sm mt-2 leading-relaxed" style={{ color: theme.inkMuted }}>
                        {item.desc}
                      </p>
                    </Link>
                  );
                })}
              </div>

              <div
                className="rounded-2xl p-6 flex flex-col justify-between"
                style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.border}` }}
              >
                <div>
                  <p
                    className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider text-xs"
                    style={{ color: theme.accent }}
                  >
                    <Sparkles size={13} />
                    Featured Product
                  </p>

                  <h3 className="text-2xl font-bold mt-3" style={{ color: theme.ink }}>UniPay Suite</h3>

                  <p className="mt-3 text-sm leading-relaxed" style={{ color: theme.inkMuted }}>
                    Complete payment, banking and verification platform for modern businesses.
                  </p>
                </div>

                <Link
                  to="/products"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="mt-6 inline-flex justify-center items-center gap-2 px-6 py-3 rounded-md text-white font-semibold hover:brightness-110 transition-all"
                >
                  Explore Products
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}